<script setup lang="ts">
// MemberInviteModal — the rebuild of `Permissions/UserPermissionModal.vue` (588 ln at HEAD, 46 %
// over the 400-line budget), the last of module 22's five panes. Seven responsibilities lived in
// that one file; five of them are now components or pure services and this is the orchestration
// that is left.
//
// ⚠ THE ONE TRAP TO UNDERSTAND BEFORE EDITING THIS FILE — the edit pre-selection.
//
// v3 seeded the form by calling `updateUserOpen()` at the TOP LEVEL of `setup()` (`:580`), and it
// worked only because `UsersPermissions.vue:121` mounts the modal behind `v-if="formModal"`. Every
// open was a fresh MOUNT, so setup — and the seeding call — ran again each time. 02's `Modal` is
// the opposite: `defineModel<boolean>` toggles a MOUNTED component's `open` state. Port the
// seeding to setup naively and it runs exactly once in the component's lifetime, so the second
// member you edit shows the FIRST member's role and scope. `MemberInviteModal.spec.ts` opens two
// different rows in one mount and asserts the second one's values, which is precisely the
// assertion a setup-time seed fails.
//
// The seam is `watch(open, …, { immediate: true })` below: it seeds on mount AND on every
// subsequent open, which is the remount semantics without the remount.
//
// The related v3 hazard, deliberately designed out rather than reproduced: the scope watcher
// (`:582-587`) would have clobbered that same pre-selection, and was saved only by declaration
// order — `watch()` is lazy and was registered AFTER the seeding call, so its first run merely
// recorded a baseline. Here the scope transition is an EVENT HANDLER (`onScopeChange`) that
// seeding never routes through, so there is no ordering to preserve.
//
// Also retired here: the two duplicate `#body` slots (`:7` and `:184`), which "worked" only
// because a later duplicate slot key overwrites the earlier one in the compiled slots object —
// rendered as two siblings they would show the form AND the results at once. `submitted` switches
// between them instead. And the cross-feature import of
// `Views/Integrations/Integrations/IntegrationModalFooter.vue` (`:271`), replaced by `Modal`'s
// own footer slot.
import { computed, reactive, ref, watch } from 'vue'

import { InviteMemberFlow } from '../../flows/InviteMemberFlow'
import { UpdateMemberFlow } from '../../flows/UpdateMemberFlow'
import type {
  AssistantSeats,
  MemberDashboardRef,
  MemberListRow,
  MemberRole,
  MemberScope,
  MemberWorkspaceRef,
} from '../../models/member.types'
import type { InviteResultDto, MemberGrantDraft } from '../../models/memberInvite.types'
import {
  assistantSeatsKey,
  bulkInvitePayload,
  classifyInviteEmail,
  draftValidationKey,
  grantPayload,
  inviteEmailTone,
  isGrantUnchanged,
  roleForScope,
  seedDraft,
  sendableEmails,
  type InviteEmailContext,
} from '../../service/inviteDraft'
import {
  classifyInviteFailure,
  failedInviteEmails,
  summarizeInvites,
} from '../../service/inviteResults'
import { foldEmail } from '../../service/memberList'

import InviteResultList from './InviteResultList.vue'
import MemberAssistantSwitch from './MemberAssistantSwitch.vue'
import MemberRolePicker from './MemberRolePicker.vue'
import MemberScopeSelector from './MemberScopeSelector.vue'

import { useAccount } from '@/features/account'
import { useI18n } from '@/shared/i18n'
import { Alert, Button, ChipInput, Modal, useToast, type UiTone } from '@/shared/ui'

const open = defineModel<boolean>({ default: false })

const props = withDefaults(
  defineProps<{
    /** The row being edited, or `null`/absent to invite. Re-read on EVERY open — see the header. */
    editing?: MemberListRow | null
    workspaces: readonly MemberWorkspaceRef[]
    /** `undefined` ⇒ the dashboard catalog could not be read (D14), NOT "there are none". */
    dashboards?: readonly MemberDashboardRef[] | undefined
    /** `useMembers().existingEmails` — already folded. Drives the `existing` chip state. */
    existingEmails: ReadonlySet<string>
    // ── MCP-only overrides (ORP-21470). Every one is OPTIONAL and absent from `PermissionsPane`,
    //    so the web settings screen renders byte-identically to what it rendered before. ────────
    /** Scopes to offer. Absent ⇒ the whole catalog. MCP passes `['account', 'workspace']`. */
    scopes?: readonly MemberScope[] | undefined
    /** Per-role description key overrides for the role picker. */
    roleDescriptionKeys?: Partial<Record<MemberRole, string>> | undefined
    /** Replaces the intro sentence. ALREADY LOCALISED — the caller owns its copy. */
    intro?: string | undefined
    /** An extra localised sentence under the form: MCP's "they connect their own assistant". */
    notice?: string | undefined
    /** Passing it — even `null` — offers the MCP switch; see `MemberGrantDraft.mcpEnabled`. */
    assistantSeats?: AssistantSeats | null | undefined
  }>(),
  { editing: null, dashboards: undefined, assistantSeats: undefined },
)

/** The member list changed on the server; the pane re-reads. Emitted on a partial invite too. */
const emit = defineEmits<{ saved: [] }>()

const { t } = useI18n()
const toast = useToast()
const { userEmail } = useAccount()

const draft = reactive<MemberGrantDraft>(seedDraft(null, false))
const emails = ref<string[]>([])
const results = ref<InviteResultDto[]>([])
const submitted = ref(false)
const pending = ref(false)
const failureKey = ref<string>()
const validationKey = ref<string>()
/** Addresses the backend just reported as `error` — exempt from the duplicate check on retry. */
const retryable = ref<ReadonlySet<string>>(new Set())

const isEditing = computed(() => props.editing !== null)
const introText = computed(() => props.intro ?? t('workspaceSettings.permissions.invite.intro'))

/* ── D8: seed on EVERY open, not once in setup ───────────────────────────────── */

function reset(): void {
  Object.assign(draft, seedDraft(props.editing, props.assistantSeats !== undefined))
  emails.value = []
  results.value = []
  submitted.value = false
  failureKey.value = undefined
  validationKey.value = undefined
  retryable.value = new Set()
}

watch(open, (isOpen) => isOpen && reset(), { immediate: true })

/* ── Chip classification ─────────────────────────────────────────────────────── */

const emailContext = computed<InviteEmailContext>(() => ({
  selfEmail: userEmail.value,
  existingEmails: props.existingEmails,
  retryable: retryable.value,
}))

/** ADMIT-then-mark: `ChipInput` gets a `chipTone`, never a `validate`, so a bad address stays
 *  visible and tinted instead of being refused at the field (v3 `:35-50`). */
function chipTone(value: string): UiTone {
  return inviteEmailTone(classifyInviteEmail(value, emailContext.value))
}

const sendable = computed(() => sendableEmails(emails.value, emailContext.value))

/** v3's blocked-address notice (`:63-79`), one reason per line. */
const blocked = computed(() => {
  const states = emails.value.map((email) => classifyInviteEmail(email, emailContext.value))
  return {
    count: states.filter((state) => state !== 'valid').length,
    self: states.includes('self'),
    invalid: states.includes('invalid'),
    existing: states.includes('existing'),
  }
})

/* ── Scope transition — an event, never a watcher (see the header) ───────────── */

function onScopeChange(scope: MemberScope): void {
  draft.scope = scope
  // Defaulted BY NAME, and here rather than inside validation, which v3 mutated from (`:462-468`).
  draft.role = roleForScope(draft.role, scope)
  // ⚠ NOT a reset to `[]`. v3 `:585` auto-selects the only workspace on a single-workspace
  // account; drop that and every invite on such an account fails "Please select Workspace".
  draft.workspaceIds = props.workspaces.length === 1 ? props.workspaces.map((w) => w.id) : []
  validationKey.value = undefined
}

/* ── D9: the rule v3 computed and never referenced ───────────────────────────── */

const unchanged = computed(() => props.editing !== null && isGrantUnchanged(draft, props.editing))

const failedCount = computed(() => summarizeInvites(results.value).failed)

/* ── Submit ──────────────────────────────────────────────────────────────────── */

async function runInvite(): Promise<void> {
  const outcome = await new InviteMemberFlow().execute(bulkInvitePayload(draft, sendable.value))
  // A partial invite is NOT a failure: the result screen is the feedback, per address.
  results.value = outcome.results
  submitted.value = true
  if (outcome.refreshFailed) toast.warning(t('workspaceSettings.permissions.refreshFailed'))
  emit('saved')
}

async function runUpdate(row: MemberListRow): Promise<void> {
  const outcome = await new UpdateMemberFlow().execute(row, grantPayload(draft))
  toast.success(t('workspaceSettings.permissions.invite.updated', { email: row.email }))
  if (outcome.refreshFailed) toast.warning(t('workspaceSettings.permissions.refreshFailed'))
  emit('saved')
  open.value = false
}

async function submit(): Promise<void> {
  const count = sendable.value.length
  const problem =
    draftValidationKey(draft, { sendableCount: count, requireEmails: !isEditing.value }) ??
    assistantSeatsKey(draft, props.assistantSeats, props.editing, count)
  validationKey.value = problem ?? undefined
  if (problem !== null) return

  failureKey.value = undefined
  pending.value = true
  try {
    if (props.editing) await runUpdate(props.editing)
    else await runInvite()
  } catch (cause) {
    failureKey.value = classifyInviteFailure(cause, isEditing.value)
  } finally {
    pending.value = false
  }
}

/**
 * Put the failed addresses back in the field.
 *
 * ⚠ v3 re-typed each one through `getChipType` (`:370-375`), and by then the parent's
 * `existingEmails` had been refreshed by the member re-read that ran on the success path. An
 * address that failed could therefore come back typed `existing`, fall out of `validChips` and
 * become silently un-retryable — visible in the field, refused by the Invite button, with no
 * message anywhere. Marking them `retryable` keeps the self/format checks (both still correct)
 * and skips only the duplicate check, which cannot be true of an address the backend refused.
 */
function retryFailed(): void {
  const failed = failedInviteEmails(results.value)
  retryable.value = new Set(failed.map(foldEmail))
  emails.value = failed
  results.value = []
  submitted.value = false
  failureKey.value = undefined
}
</script>

<template>
  <Modal
    v-model="open"
    size="lg"
    :title="
      isEditing
        ? t('workspaceSettings.permissions.invite.updateTitle')
        : t('workspaceSettings.permissions.invite.title')
    "
    data-testid="member-invite-modal"
  >
    <InviteResultList v-if="submitted" :results="results" />

    <div v-else class="invite-form">
      <p class="invite-form__intro">{{ introText }}</p>

      <Alert v-if="failureKey" tone="danger" data-testid="invite-error">
        {{ t(failureKey) }}
      </Alert>
      <Alert v-if="validationKey" tone="warning" data-testid="invite-validation">
        {{ t(validationKey) }}
      </Alert>

      <!-- The address is immutable in edit mode — v3 rendered a disabled box (`:11-24`). -->
      <div v-if="isEditing" class="invite-form__email" data-testid="invite-locked-email">
        <span class="invite-form__email-label">
          {{ t('workspaceSettings.permissions.invite.emailLabel') }}
        </span>
        {{ props.editing?.email }}
      </div>

      <template v-else>
        <ChipInput
          v-model="emails"
          :label="t('workspaceSettings.permissions.invite.emailLabel')"
          :placeholder="t('workspaceSettings.permissions.invite.emailPlaceholder')"
          :delimiters="[',', ';', ' ']"
          :normalize="foldEmail"
          :chip-tone="chipTone"
          add-on-blur
          :disabled="pending"
          data-testid="invite-emails"
        />

        <Alert v-if="blocked.count > 0" tone="warning" data-testid="invite-blocked">
          <p class="invite-form__blocked-title">
            {{ t('workspaceSettings.permissions.invite.blocked', { count: blocked.count }) }}
          </p>
          <p v-if="blocked.self">{{ t('workspaceSettings.permissions.invite.blockedSelf') }}</p>
          <p v-if="blocked.invalid">
            {{ t('workspaceSettings.permissions.invite.blockedInvalid') }}
          </p>
          <p v-if="blocked.existing">
            {{ t('workspaceSettings.permissions.invite.blockedExisting') }}
          </p>
        </Alert>
      </template>

      <MemberScopeSelector
        v-model:workspace-ids="draft.workspaceIds"
        v-model:dashboard-ids="draft.dashboardIds"
        :scope="draft.scope"
        :workspaces="props.workspaces"
        :dashboards="props.dashboards"
        :scopes="props.scopes"
        :disabled="pending"
        @update:scope="onScopeChange"
      />

      <MemberRolePicker
        v-model="draft.role"
        :scope="draft.scope"
        :description-keys="props.roleDescriptionKeys"
        :disabled="pending"
      />

      <MemberAssistantSwitch
        v-if="draft.mcpEnabled !== null"
        v-model="draft.mcpEnabled"
        :scope="draft.scope"
        :seats="props.assistantSeats ?? null"
        :disabled="pending"
      />

      <p v-if="props.notice" class="invite-form__intro" data-testid="invite-notice">
        {{ props.notice }}
      </p>
    </div>

    <template #footer>
      <template v-if="submitted">
        <Button
          v-if="failedCount > 0"
          variant="outline"
          data-testid="invite-retry"
          @click="retryFailed"
        >
          {{ t('workspaceSettings.permissions.invite.retry', { count: failedCount }) }}
        </Button>
        <Button variant="primary" data-testid="invite-done" @click="open = false">
          {{ t('workspaceSettings.permissions.invite.done') }}
        </Button>
      </template>

      <template v-else>
        <Button variant="soft" :disabled="pending" @click="open = false">
          {{ t('workspaceSettings.common.cancel') }}
        </Button>
        <Button
          variant="primary"
          :loading="pending"
          :disabled="unchanged"
          data-testid="invite-submit"
          @click="submit"
        >
          {{
            isEditing
              ? t('workspaceSettings.common.update')
              : t('workspaceSettings.permissions.invite.action')
          }}
        </Button>
      </template>
    </template>
  </Modal>
</template>

<style scoped>
.invite-form {
  display: flex;
  flex-direction: column;
  gap: var(--ui-space-md);
  font-family: var(--ui-font-sans);
}

.invite-form__intro {
  margin: 0;
  color: var(--ui-color-muted);
  font-size: 0.8125rem;
}

.invite-form__email {
  padding: var(--ui-space-xs) var(--ui-space-sm);
  border: 1px solid var(--ui-color-border);
  border-radius: var(--ui-radius-md);
  background: var(--ui-color-surface-sunken);
  color: var(--ui-color-muted);
  font-size: 0.8125rem;
}

.invite-form__email-label {
  display: block;
  margin-bottom: var(--ui-space-2xs);
  font-size: 0.6875rem;
  font-weight: 600;
}

.invite-form__blocked-title {
  margin: 0;
  font-weight: 600;
}
</style>
